<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import Icon from 'next/icon/Icon.vue';
import { useSidebarContext } from './provider';
import SidebarPinButton from './SidebarPinButton.vue';
import SidebarUnreadBadge from './SidebarUnreadBadge.vue';

const props = defineProps({
  item: { type: Object, required: true },
  collapsed: { type: Boolean, default: false },
});

const route = useRoute();
const { resolvePath } = useSidebarContext();

// Contadores vêm do `badgeCount` do item ou de um getter da store (ex.: Tarefas)
const getterCount = useMapGetter(props.item.getterKeys?.count);
const count = computed(() => props.item.badgeCount || getterCount.value || 0);

const isActive = computed(() => {
  const { to, activeOn = [] } = props.item;
  if (route.path === resolvePath(to)) return true;
  if (!activeOn.includes(route.name)) return false;
  return Object.entries(to.params ?? {}).every(
    ([key, value]) => String(value) === String(route.params[key])
  );
});
</script>

<template>
  <li class="relative min-w-0 list-none group/pin">
    <router-link
      v-if="collapsed"
      :to="item.to"
      :title="item.fullLabel"
      draggable="false"
      class="flex items-center justify-center rounded-lg size-10"
      :class="
        isActive
          ? 'text-n-slate-12 bg-n-alpha-2'
          : 'text-n-slate-11 hover:bg-n-alpha-2'
      "
    >
      <Icon v-if="item.icon" :icon="item.icon" class="size-4" />
    </router-link>
    <template v-else>
      <!-- O botão vem antes do link para o `peer-*` do link enxergá-lo -->
      <SidebarPinButton :pin-key="item.key" />
      <router-link
        :to="item.to"
        :title="item.fullLabel"
        draggable="false"
        class="flex items-center h-8 min-w-0 gap-2 px-2 py-1 pe-7 rounded-xl"
        :class="
          isActive
            ? 'text-n-slate-12 ring-1 ring-inset ring-n-blue-border [background-color:color-mix(in_srgb,var(--color-brand-secondary)_15%,transparent)] dark:[background-color:color-mix(in_srgb,var(--color-brand-secondary)_20%,transparent)]'
            : 'text-n-slate-11 hover:bg-n-alpha-2'
        "
      >
        <span
          v-if="item.icon"
          class="grid rounded-full size-4 place-content-center"
        >
          <Icon :icon="item.icon" class="inline-block size-4" />
        </span>
        <div class="flex-1 min-w-0 text-sm truncate">{{ item.fullLabel }}</div>
        <SidebarUnreadBadge :count="count" />
      </router-link>
    </template>
  </li>
</template>
